import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useParams } from "react-router-dom";

import { useAnniversaryCampaignContext } from "../../context/AnniversaryCampaignContext";
// import { useAuthContext } from "../../context/AuthContext";
import { useGeneralContext } from "../../context/GeneralContext";

import useNextVideo from "../../hooks/useNextVideo";
import { formatCourseTime, getProgress } from "../../util/course";

import Container from "../../components/Container";
import CourseBanner from "../../components/CourseBanner";
import Errors from "../../components/Errors";
import CourseButton from "../../components/CourseButton";
import Banner from "../../components/Banner";
import Tabs from "../../components/Tabs";
import CourseAbout from "../../components/CourseAbout";
import CourseVideos from "../../components/CourseVideos";
import Player from "../../components/Player";
import ProgressBar from "../../components/ProgressBar";

import { ReactComponent as ClassesIcon } from "../../assets/icons/classes-icon.svg";
import { ReactComponent as ClockIcon } from "../../assets/icons/total-class-hours-icon.svg";
import playIcon from "../../assets/icons/play-black-icon.svg";

import { requests } from "../../services/requests";
import * as S from "./style";

const FolderContentsList = () => {
  const { slug } = useParams();
  const { search } = useLocation();

  const [fetchingCourse, setFetchingCourse] = useState(true);
  const [course, setCourse] = useState({});
  const [videoSlug, setVideoSlug] = useState(null);
  const [ignoreProgressOnAutoplay, setIgnoreProgressOnAutoplay] =
    useState(false);

  const { allCourses, generalContextLoading } = useGeneralContext();
  const { availableCoursesIds, anniversaryCourses, coursesReleaseDate } =
    useAnniversaryCampaignContext();

  // CAMPANHA
  const isLoggedIn = false;
  // const { isLoggedIn } = useAuthContext();

  const currentCourse = allCourses.find((course) => course.slug === slug);
  const courseId = !!currentCourse && currentCourse.id;
  const isAnniversaryCourse = anniversaryCourses.some(
    (course) => course.id === courseId
  );
  const isCourseAvailable =
    isAnniversaryCourse && availableCoursesIds.some((id) => id === courseId);
  const hasContent = !!course.id;

  // Próximo vídeo e texto do botão
  const { nextVideo, playButtonText } = useNextVideo(
    course.contents,
    course.isFinished
  );

  const getContent = async () => {
    try {
      if (!courseId) throw new Error();

      if (isLoggedIn) {
        const personalCourse = await requests.getPersonalCourse(courseId);
        setCourse(personalCourse);
      } else {
        const publicCourse = await requests.getCourse(courseId);
        setCourse(publicCourse);
      }

      setFetchingCourse(false);
    } catch (e) {
      console.error("ERRO: ", e);
      setFetchingCourse(false);
    }
  };

  const playVideo = (slug) => {
    setIgnoreProgressOnAutoplay(false);
    if (slug) {
      setVideoSlug(slug);
      return;
    }

    const { slug: nextVideoSlug } = nextVideo();
    setVideoSlug(nextVideoSlug);
  };

  const autoplayNextVideo = () => {
    const index =
      !!course &&
      course.contents.findIndex((video) => video.slug === videoSlug);
    const isLast = index === course.contents.length - 1;
    if (isLast) {
      getContent();
      return setVideoSlug(null);
    }
    setIgnoreProgressOnAutoplay(true);
    return setVideoSlug(course.contents[index + 1]?.slug);
  };

  // busca o id do curso para passar pro player
  const currentClass = useMemo(() => {
    if (course.contents) {
      return course.contents.find((content) => content.slug === videoSlug);
    }
  }, [videoSlug, course]);

  useEffect(() => {
    if (!generalContextLoading) {
      getContent();
    }
  }, [generalContextLoading]);

  const suffix = Number(course.classes) > 1 ? "s" : "";

  const { status, percentage } = getProgress(
    course.classesFinished,
    course.classes
  );

  const renderProgressContainer = {
    inProgress: (
      <S.ProgressContainer>
        <ProgressBar progress={percentage} />
        Aula{" "}
        <strong>
          {course.classesFinished}/{course.classes}
        </strong>
      </S.ProgressContainer>
    ),
    concluded: (
      <S.ProgressContainer>
        <ProgressBar progress={percentage} />
        <span>
          <strong>100% concluído</strong>
        </span>
        <span>
          Aula{" "}
          <strong>
            {course.classesFinished}/{course.classes}
          </strong>
        </span>
      </S.ProgressContainer>
    ),
    notStarted: <></>,
    default: <></>,
  };

  const renderButton = () => {
    // Se não é curso do aniversário ou usuário não logado,
    // leva para checkout ondemand
    if (!isAnniversaryCourse || !isLoggedIn) {
      return (
        <S.ButtonLink
          to={{
            pathname: "/signup",
            search,
          }}
        >
          <CourseButton
            text={"Clique para assinar"}
            variant={"white"}
            size={"full"}
            icon={"locked"}
          />
        </S.ButtonLink>
      );
    }

    if (isAnniversaryCourse) {
      // Se liberado e logado, assiste o curso
      if (isCourseAvailable)
        return (
          <S.ButtonContainer>
            <CourseButton
              text={playButtonText()}
              variant={"white"}
              size={"full"}
              icon={playIcon}
              onClick={() => playVideo()}
            />
          </S.ButtonContainer>
        );

      // Se não liberado e logado, informa que estará disponível em breve
      if (!isCourseAvailable) {
        const releaseDate = coursesReleaseDate.find(
          (item) => item.id === courseId
        ).date;

        const data = releaseDate.toLocaleString("pt-br", {
          month: "2-digit",
          day: "2-digit",
        });

        return (
          <S.ButtonContainer>
            <CourseButton
              text={`Disponível em ${data}`}
              variant={"white"}
              size={"full"}
            />
          </S.ButtonContainer>
        );
      }
    }
  };

  const renderPageContent = () => {
    if (fetchingCourse || generalContextLoading) {
      return (
        <S.SpinnerContainer>
          <div className={"spinner"} />
        </S.SpinnerContainer>
      );
    }

    if (!hasContent) {
      return <Errors status={404} />;
    }

    return (
      <div className="page-content">
        {!!isLoggedIn && isAnniversaryCourse ? (
          <CourseBanner
            coverDesktop={course.cover}
            coverMobile={course.coverMobileUrl || course.cover}
            isCourseLoading={fetchingCourse}
          />
        ) : (
          <Banner content={course} />
        )}

        <S.CourseContent>
          <S.CourseHeader>
            <div>
              <S.CourseTitle>{course.title}</S.CourseTitle>
              <S.CourseSubtitle>por {course.author}</S.CourseSubtitle>
            </div>

            <S.CourseDuration>
              <div>
                <ClockIcon /> {formatCourseTime(course.courseDuration)}
              </div>
              <div>
                <ClassesIcon /> {course.classes} aula{suffix}
              </div>
            </S.CourseDuration>

            {renderButton()}

            {renderProgressContainer[status]}

            {!!course.complementaryFile && (
              <S.AnchorDownload
                download={`${course.title}.pdf`}
                href={course.complementaryFile}
                target={"_blank"}
                rel="noreferrer noopener"
              >
                Baixar material de apoio
              </S.AnchorDownload>
            )}
          </S.CourseHeader>

          <Tabs
            titles={["Aulas", "Sobre"]}
            contents={[
              <CourseVideos
                content={course.contents}
                playVideo={playVideo}
                hasAccess={isLoggedIn && isCourseAvailable}
                isAutoPlay={true}
              />,
              <CourseAbout
                description={course.description}
                author={course.author}
                authorId={course.authorId}
                authorDescription={course.authorDescription}
                authorPhoto={course.authorThumb}
              />,
            ]}
          />
        </S.CourseContent>
      </div>
    );
  };

  return (
    <Container type={"transparent"}>
      {renderPageContent()}

      {/* Só vai executar se houver videoSlug */}
      <Player
        video={currentClass}
        courseId={course.id}
        onClose={() => {
          getContent();
          setVideoSlug(null);
        }}
        autoplayNextVideo={autoplayNextVideo}
        ignoreProgress={ignoreProgressOnAutoplay}
        playerKey={course.playerKey}
      />
    </Container>
  );
};

export default FolderContentsList;
